import { Chip } from '@crowdex/ui/components/chip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@crowdex/ui/components/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import { CircleX, MessageSquarePlus, Search } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import {
  SIDEBAR_SEARCH_SHORTCUT,
  setSidebarSearchOpen,
  useSidebarSearch,
} from '@/lib/desktop-shell/sidebar-search';
import { matchesShortcut } from '@/lib/platform';
import {
  COMMAND_PALETTE_SHORTCUT,
  currentShortcut,
  shortcutAllowedFrom,
} from '@/lib/settings-page/keyboard-shortcuts';
import { type ActionEntry, useSearchEntries, useStartConversation } from './search-entries';
import { highlightParts, SEARCH_KINDS, type SearchFilter, searchResults } from './search-model';
import { useShellNavigation } from './shell-navigation';

/*
 * Search & commands (D21, mobile 18): one dialog for the conversations, the artifacts, the
 * files and the commands (the page's own items, the apps' panels, the shell's actions). ↵ opens,
 * ⌥↵ opens a conversation in split view. ⌘K toggles it, ⌘⇧P opens it on the commands.
 */

let filterState: SearchFilter = 'all';
const filterListeners = new Set<() => void>();
/** The kind search opens on next: the dialog's, and the phone's screen as it opens. */
export function setSearchFilter(next: SearchFilter) {
  filterState = next;
  for (const listener of filterListeners) listener();
}
function useSearchFilter(): SearchFilter {
  return useSyncExternalStore(
    (listener) => {
      filterListeners.add(listener);
      return () => filterListeners.delete(listener);
    },
    () => filterState,
    () => 'all' as const,
  );
}

/** Opens search & commands, on one kind (the View's archived conversations, ⌘⇧P). */
export function openShellSearch({ filter = 'all' }: { filter?: SearchFilter } = {}): void {
  setSearchFilter(filter);
  setSidebarSearchOpen(true);
}

/** ⌘K toggles the dialog from anywhere; ⌘⇧P (and the palette event) opens it on commands. */
export function useShellSearchShortcuts(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !shortcutAllowedFrom(event)) return;
      // The keys Settings › Keyboard shortcuts gives them, else ⌘K and ⇧⌘P.
      if (matchesShortcut(event, currentShortcut('search') ?? SIDEBAR_SEARCH_SHORTCUT)) {
        event.preventDefault();
        setSearchFilter('all');
        setSidebarSearchOpen((current) => !current);
      } else if (
        matchesShortcut(event, currentShortcut('command-palette') ?? COMMAND_PALETTE_SHORTCUT)
      ) {
        event.preventDefault();
        openShellSearch({ filter: 'commands' });
      }
    };
    const onPalette = () => openShellSearch({ filter: 'commands' });
    window.addEventListener('keydown', onKey);
    window.addEventListener('crowdex-open-command-palette', onPalette);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('crowdex-open-command-palette', onPalette);
    };
  }, []);
}

function Highlighted({ text, query }: { text: string; query: string }) {
  // Keyed by where each part starts in the text: the parts follow one another.
  const nodes: ReactNode[] = [];
  let offset = 0;
  for (const part of highlightParts(text, query)) {
    nodes.push(
      part.match ? (
        <strong className="font-semibold text-foreground" key={offset}>
          {part.text}
        </strong>
      ) : (
        <span key={offset}>{part.text}</span>
      ),
    );
    offset += part.text.length;
  }
  return <>{nodes}</>;
}

function FilterPills({
  filter,
  counts,
  onChange,
  density,
}: {
  filter: SearchFilter;
  counts: Record<string, number>;
  onChange: (filter: SearchFilter) => void;
  density: 'desktop' | 'phone';
}) {
  const pills: { value: SearchFilter; label: string; count?: number }[] = [
    { value: 'all', label: 'All' },
    ...SEARCH_KINDS.map(({ kind, label }) => ({ value: kind, label, count: counts[kind] ?? 0 })),
  ];
  // The chosen pill in sight, though the row scrolls (opened on Archived, its last).
  const row = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies(filter): another pill is chosen, found by its aria-selected.
  // biome-ignore lint/correctness/useExhaustiveDependencies(counts[filter]): its count shows or goes, which moves it in the row.
  useEffect(() => {
    row.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [filter, counts[filter]]);
  return (
    // On a touch screen, room above and below for the chips' 44 px touch area (the row scrolls,
    // so it clips them), without moving them.
    <div
      aria-label="Filter"
      ref={row}
      className="flex gap-1.5 overflow-x-auto pointer-coarse:-my-1 pointer-coarse:py-1"
      role="tablist"
    >
      {pills
        .filter((pill) => pill.value === 'all' || pill.value === filter || (pill.count ?? 0) > 0)
        .map((pill) => (
          <Chip
            className={density === 'desktop' ? 'type-ui' : 'h-9'}
            key={pill.value}
            onClick={() => onChange(pill.value)}
            role="tab"
            selected={filter === pill.value}
            size="md"
          >
            {pill.label}
            {pill.count !== undefined && pill.count > 0 && (
              <span className="text-muted-foreground tabular-nums">{pill.count}</span>
            )}
          </Chip>
        ))}
    </div>
  );
}

/** The search & commands dialog (D21) of the desktop shell. */
export function ShellSearchDialog() {
  const { open } = useSidebarSearch();
  const filter = useSearchFilter();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('');
  const entries = useSearchEntries(open, query);
  const { errors } = useWorkbench();
  const navigation = useShellNavigation();
  const { groups, counts } = useMemo(
    () => searchResults(entries, query, filter),
    [entries, query, filter],
  );
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  const run = (entry: ActionEntry, sideBySide: boolean) => {
    // A dialog it opens keeps the focus: given back to the search's opener, it would close it.
    if (entry.opensDialog === true) keepFocusRef.current = true;
    setSidebarSearchOpen(false);
    if (sideBySide && entry.openSideBySide !== undefined) entry.openSideBySide();
    else entry.open();
  };
  const find = (value: string) =>
    groups.flatMap((group) => group.entries).find((entry) => entry.id === value);
  const startConversation = useStartConversation();
  // A conversation started from here keeps the focus its composer takes, a dialog opened from
  // here its own (M4).
  const keepFocusRef = useRef(false);
  const typed = query.trim();
  // Nothing but the commands always offered: say so, and offer to start with what was typed.
  const nothingFound =
    typed !== '' && groups.every((group) => group.entries.every((entry) => entry.always === true));
  return (
    <Dialog onOpenChange={(next) => setSidebarSearchOpen(next)} open={open}>
      <DialogContent
        aria-describedby={undefined}
        className="top-24 max-w-[680px] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-[680px]"
        onCloseAutoFocus={(event) => {
          if (!keepFocusRef.current) return;
          keepFocusRef.current = false;
          event.preventDefault();
        }}
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Search and commands</DialogTitle>
        <DialogDescription className="sr-only">
          Conversations, artifacts, files and commands
        </DialogDescription>
        <Command
          className="flex flex-col border-0 bg-transparent shadow-none [&_[data-slot=command-input-wrapper]]:h-14 [&_[data-slot=command-input-wrapper]]:gap-3 [&_[data-slot=command-input-wrapper]]:border-hairline [&_[data-slot=command-input-wrapper]]:px-4"
          label="Search and commands"
          loop
          onKeyDown={(event) => {
            if (event.key === 'Enter' && event.altKey) {
              const entry = find(selected);
              if (entry !== undefined) {
                event.preventDefault();
                run(entry, true);
              }
            }
          }}
          onValueChange={setSelected}
          shouldFilter={false}
          value={selected}
        >
          <div className="relative">
            <CommandInput
              className="pr-12 text-base"
              data-appearance="document"
              onValueChange={setQuery}
              placeholder="Search or start a conversation"
              value={query}
            />
            <kbd className="type-caption pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 rounded border border-hairline px-1.5 py-0.5 text-muted-foreground">
              Esc
            </kbd>
          </div>
          <div className="px-3 pt-3 pb-1">
            <FilterPills
              counts={nothingFound ? { ...counts, commands: 0 } : counts}
              density="desktop"
              filter={filter}
              onChange={setSearchFilter}
            />
          </div>
          <CommandList className="max-h-[min(26rem,60dvh)] px-1.5 pb-1.5">
            {nothingFound ? (
              <>
                <p className="type-ui px-2.5 pt-2 pb-1 text-muted-foreground" role="status">
                  No results for “{typed}”
                </p>
                <CommandGroup className="p-0.5">
                  <CommandItem
                    className="type-ui h-9 gap-3 rounded-lg px-2.5 text-foreground/90"
                    onSelect={() => {
                      keepFocusRef.current = true;
                      setSidebarSearchOpen(false);
                      startConversation(typed);
                    }}
                    value="start-conversation"
                  >
                    <MessageSquarePlus />
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                      Start a conversation: “{typed}”
                    </span>
                  </CommandItem>
                </CommandGroup>
              </>
            ) : (
              <CommandEmpty>Nothing found.</CommandEmpty>
            )}
            {!nothingFound &&
              groups.map((group) => (
                <CommandGroup
                  className="p-0.5"
                  heading={
                    query.trim() === '' ? group.label : `${group.label} · ${counts[group.kind]}`
                  }
                  key={group.kind}
                >
                  {group.entries.map((entry) => (
                    <CommandItem
                      className="type-ui h-9 gap-3 rounded-lg px-2.5 text-foreground/90"
                      key={entry.id}
                      onSelect={() => run(entry, false)}
                      value={entry.id}
                    >
                      {entry.icon}
                      <span className="shrink-0 truncate font-medium text-foreground max-w-[55%]">
                        <Highlighted query={query} text={entry.label} />
                      </span>
                      {entry.detail !== undefined && (
                        <span className="type-caption min-w-0 flex-1 truncate text-muted-foreground">
                          {entry.detail}
                        </span>
                      )}
                      {entry.detail === undefined && <span className="flex-1" />}
                      {entry.meta !== undefined && (
                        <span className="type-caption shrink-0 text-muted-foreground tabular-nums">
                          {entry.meta}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
          </CommandList>
          {/* A preview has no session by design: what failed for want of one is not said. */}
          {(navigation.preview ? [] : errors).map((error) => (
            <p className="type-caption px-4 pb-1.5 text-destructive" key={error} role="alert">
              {error}
            </p>
          ))}
          <p className="type-caption border-hairline border-t px-4 py-2 text-muted-foreground">
            ↵ open · ⌥↵ open in split view
          </p>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** Search on a phone (18): full screen, its field at the top with Cancel. */
export function ShellSearchScreen({
  onClose,
  onLeave = (then) => {
    onClose();
    then();
  },
}: {
  onClose: () => void;
  /** Closes the screen (and what pushed it), then opens what was chosen. */
  onLeave?: (then: () => void) => void;
}) {
  const [query, setQuery] = useState('');
  // On the kind it was opened on (the View's archived conversations); a phone has no commands.
  const [filter, setFilter] = useState<SearchFilter>(() =>
    filterState === 'commands' ? 'all' : filterState,
  );
  useEffect(() => setSearchFilter('all'), []);
  const entries = useSearchEntries(true, query);
  const startConversation = useStartConversation();
  const { groups, counts } = useMemo(
    () =>
      searchResults(
        entries
          .filter((entry) => entry.kind !== 'commands')
          // No key to press on a phone: a tap brings an archived conversation back.
          .map((entry) =>
            entry.kind === 'archived'
              ? { ...entry, detail: 'Archived · tap to bring it back' }
              : entry,
          ),
        query,
        filter,
      ),
    [entries, query, filter],
  );
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex shrink-0 items-center gap-2 px-4 pt-3 pb-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search"
            className="h-11 rounded-control border-running/70 bg-card pr-10 pl-10 text-base"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            ref={(input) => input?.focus()}
            value={query}
          />
          {query !== '' && (
            <button
              aria-label="Clear"
              className="absolute top-1/2 right-0 flex size-11 -translate-y-1/2 items-center justify-center text-muted-foreground"
              onClick={() => setQuery('')}
              type="button"
            >
              <CircleX className="size-4" />
            </button>
          )}
        </div>
        <button className="min-h-11 px-1 text-foreground" onClick={onClose} type="button">
          Cancel
        </button>
      </div>
      <div className="shrink-0 px-4 pb-2">
        <FilterPills
          counts={{ ...counts, commands: 0 }}
          density="phone"
          filter={filter}
          onChange={setFilter}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
        {groups.length === 0 &&
          (query.trim() === '' ? (
            <p className="py-10 text-center text-muted-foreground">Nothing found.</p>
          ) : (
            <div className="flex flex-col items-center gap-3 py-10">
              <p className="text-center text-muted-foreground" role="status">
                No results for “{query.trim()}”
              </p>
              <button
                className="flex min-h-11 items-center gap-2 rounded-control bg-control/70 px-4 text-foreground active:bg-control"
                onClick={() => onLeave(() => startConversation(query))}
                type="button"
              >
                <MessageSquarePlus className="size-4" />
                <span className="min-w-0 truncate">Start a conversation: “{query.trim()}”</span>
              </button>
            </div>
          ))}
        {groups.map((group) => (
          <section aria-label={group.label} className="mt-3" key={group.kind}>
            <h2 className="type-meta mb-1 text-muted-foreground">
              {group.label} · {counts[group.kind]}
            </h2>
            <ul className="divide-y divide-hairline">
              {group.entries.map((entry) => (
                <li key={entry.id}>
                  <button
                    className="flex w-full items-start gap-3 py-3 text-left active:bg-accent/40"
                    onClick={() => onLeave(entry.open)}
                    type="button"
                  >
                    {group.kind !== 'conversations' && (
                      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-5">
                        {entry.icon}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate font-semibold',
                            group.kind === 'files' && 'font-mono font-normal',
                          )}
                        >
                          <Highlighted query={query} text={entry.label} />
                        </span>
                        {entry.meta !== undefined && (
                          <span className="type-meta shrink-0 text-muted-foreground">
                            {entry.meta}
                          </span>
                        )}
                      </span>
                      {entry.detail !== undefined && (
                        <span className="type-meta block truncate text-muted-foreground">
                          {entry.detail}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
